import { useState, Fragment, useEffect } from 'react';
import { Auth } from "../../../Common/lib/auth";
import { API } from "../../../Common/lib/api";
import { Dialog, Transition } from '@headlessui/react'

import { ImSpinner8 } from "react-icons/im";
import { HiOutlineCheck } from 'react-icons/hi';

export default function RequestVerificationModal({
  setOpenVerificationModal,
  openVerificationModal,
  userProfile,
  checkExistingRequests,
  userInfo = {}, // default to an empty object if userInfo is not passed
  setUserInfo = () => { } // Default to a no-op function
}) {
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [fieldOfResearch, setFieldOfResearch] = useState(userProfile.research_field || "");
  const [geoLocationOfResearch, setGeoLocationOfResearch] = useState(userProfile.research_geo || "");
  const [institute, setInstitute] = useState(userProfile.institute || "");
  const [fieldOfResearchError, setFieldOfResearchError] = useState("");
  const [geoLocationOfResearchError, setGeoLocationOfResearchError] = useState("");
  const [instituteError, setInstituteError] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [requestSubmitted, setRequestSubmitted] = useState(false); // Used for different modal "pages". If true it sets the modal page to the success page.
  const [requestButtonDisabled, setRequestButtonDisabled] = useState(false);

  const [loading, setLoading] = useState(false); // Used for loading spinner

  useEffect(() => {
    setFieldOfResearch(userProfile.research_field);
    setGeoLocationOfResearch(userProfile.research_geo);
    setInstitute(userProfile.institute);
  }, [userProfile]);

  /**
   * Creates an item an item in the Administration table that contains user information for an admin/mod to review and accept/deny.
   */
  async function requestVerification() {
    setFormSubmitted(true);
    let errors = false;
    const updatedInfo = {
      ...userInfo,
      fieldOfResearch: fieldOfResearch,
      geoLocationOfResearch: geoLocationOfResearch
    };

    setUserInfo(updatedInfo);

    setRequestButtonDisabled(true); // Disables the request button to prevent spamming
    setLoading(true);
    setErrorMessage("");
    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, {
      research_field: fieldOfResearch,
      research_geo: geoLocationOfResearch,
      institute: institute
    });


    if (!fieldOfResearch) {
      setFieldOfResearchError("Field of Research is required.");
      errors = true;
    } else {
      setFieldOfResearchError("");
    }

    if (!geoLocationOfResearch) {
      setGeoLocationOfResearchError("Geographic Location of Research is required.");
      errors = true;
    } else {
      setGeoLocationOfResearchError("");
    }

    if (!institute) {
      setInstituteError("University/Research Institute is required.");
      errors = true;
    } else {
      setInstituteError("");
    }

    if (errors) return;

    // Create the infoObject with the additional fields
    // Object containing all the user info that gets sent inside "userInfo"
    let infoObject = {
      userInfo: {
        name: `${userProfile.fname} ${userProfile.lname}`,
        email: userProfile.email,
      },
      info: additionalInfo
    };

    try {
      await API.logs.create({
        category: "VerificationRequest",
        Date: Date.now(),
        userID: userProfile.username,
        status: "under review",
        userInfo: JSON.stringify(infoObject),
      });
      checkExistingRequests();
      setRequestSubmitted(true); // setting this to true makes it go to the success page
    } catch (e) {
      setErrorMessage("Unable to send request");
      setRequestButtonDisabled(false);
      // console.log(e.message);
    }
    setLoading(false);
  }

  /**
   * Handles displaying the current page of the verification process (2 pages). 
   * Page 1: page with relevant user information (show this page if request hasn't been submitted yet). 
   * Page 2: Success page (show this page once the user sends requests and it succeeds).
   * @returns Current Verification Page Component
   */
  function modalContents() {
    if (!requestSubmitted) {
      // Request verification page
      return (
        <>
          <div>
            <div className="mt-3 text-center sm:mt-5">
              <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                Request Verification
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please make sure your information is correct before requesting verification
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">

            <div className="mt-12 w-full grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
              <p>
                <span className="block font-medium text-gray-900 dark:text-white">First Name</span>
                <span className="block text-gray-900 text-sm dark:text-gray-400">
                  {userProfile.fname}
                </span>
              </p>
              <p>
                <span className="block font-medium text-gray-900 dark:text-white">Last Name</span>
                <span className="block text-gray-900 text-sm dark:text-gray-400">
                  {userProfile.lname}
                </span>
              </p>
            </div>

            <div className="mt-12 sm:col-span-6">
              <label htmlFor="research_field" className="block text-base font-medium text-gray-900 dark:text-white">
                Field of Research
              </label>
              <div className="mt-1">
                <input
                  id="research_field"
                  type="text"
                  value={fieldOfResearch}
                  onChange={(e) => setFieldOfResearch(e.target.value)}
                  className={`py-3 px-4 block w-full shadow-sm focus:ring-blue-500 border focus:border-blue-500 ${fieldOfResearchError ? "border-red-500 dark:border-red-500" : "border-gray-300 dark:border-gray-600"} rounded-md dark:bg-gray-700 dark:text-white`}
                />
                {fieldOfResearchError && <div className="text-red-600 text-sm">{fieldOfResearchError}</div>}
              </div>
            </div>

            <div className="mt-12 sm:col-span-6">
              <label htmlFor="research_geo" className="block text-base font-medium text-gray-900 dark:text-white">
                Geographic Location of Research
              </label>
              <div className="mt-1">
                <input
                  id="research_geo"
                  type="text"
                  value={geoLocationOfResearch}
                  onChange={(e) => setGeoLocationOfResearch(e.target.value)}
                  className={`py-3 px-4 block w-full shadow-sm focus:ring-blue-500 border focus:border-blue-500 ${geoLocationOfResearchError ? "border-red-500 dark:border-red-500" : "border-gray-300 dark:border-gray-600"} rounded-md dark:bg-gray-700 dark:text-white`}
                />
                {geoLocationOfResearchError && <div className="text-red-600 text-sm">{geoLocationOfResearchError}</div>}
              </div>
            </div>

            <div className="mt-12 sm:col-span-6">
              <label htmlFor="institute" className="block text-base font-medium text-gray-900 dark:text-white">
                University/Research Institute
              </label>
              <div className="mt-1">
                <input
                  id="institute"
                  type="text"
                  value={institute}
                  onChange={(e) => setInstitute(e.target.value)}
                  className={`py-3 px-4 block w-full shadow-sm focus:ring-blue-500 border focus:border-blue-500 ${instituteError ? "border-red-500 dark:border-red-500" : "border-gray-300 dark:border-gray-600"} rounded-md dark:bg-gray-700 dark:text-white`}
                />
                {instituteError && <div className="text-red-600 text-sm">{instituteError}</div>}
              </div>
            </div>


            <div className="mt-12 sm:col-span-6">
              <label
                htmlFor="info"
                className="block text-base font-medium text-gray-900 dark:text-white"
              >
                Additional Info
              </label>
              <div className="mt-1">
                <textarea
                  id="info"
                  rows="2"
                  name="info"
                  className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-12 grid sm:space-x-3 space-x-0 sm:grid-cols-2">
              <button
                type="button"
                className="inline-flex items-center w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-1"
                onClick={() => { requestVerification(); }}
                disabled={requestButtonDisabled}
              >
                Request Verification
                {loading &&
                  <ImSpinner8 className="w-5 h-5 text-white animate-spin ml-2" />
                }
              </button>
              <button
                type="button"
                className="mt-2 inline-flex w-full justify-center rounded-md text-gray-700 bg-gray-300 px-3 py-2 text-sm font-semibold shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:bg-gray-400 sm:col-start-2 sm:mt-0 dark:border dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                onClick={() => setOpenVerificationModal(false)}
              >
                Cancel
              </button>
            </div>
            {errorMessage.length > 0 && (
              <p className="mt-4 italic text-xs sm:text-sm block text-center font-base font-normal text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
        </>
      );
    }
    else {
      // Success page
      return (
        <>
          <div>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-200">
              <HiOutlineCheck className="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                Verification Request Sent
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your request is under review.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">
            <button
              type="button"
              className="mb-2 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => setOpenVerificationModal(false)}
            >
              Done
            </button>
          </div>
        </>
      );
    }
  }

  return (
    <>
      {/* Modal */}
      <Transition.Root show={openVerificationModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpenVerificationModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-900">
                  {modalContents()}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
/* 
ඞ
*/